/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    DataImportWizardState,
    UploadedFileMeta,
    WizardFileId,
    FileImportState,
    EquipmentField,
    EquipmentRowData
} from "./types";
import {
    importEquipments,
    revalidateEquipment,
    uploadEquipmentFiles,
    validateEquipments
} from "./dataImportWizardApi";

const initialState: DataImportWizardState = {
    files: [],
    validations: {},
    imports: {},
    import: { status: "idle", progress: 0, done: false }
};

export const uploadFiles = createAsyncThunk(
    "dataImportWizard/uploadFiles",
    async (files: File[], { dispatch }) => {
        const metas = await uploadEquipmentFiles(files);
        dispatch(setFiles(metas));
        return metas;
    }
);

const fileKey = (f: File) => `${f.name}-${f.size}-${(f as any)?.lastModified ?? ""}`;

export const syncFilesWithUpload = createAsyncThunk(
    "dataImportWizard/syncFilesWithUpload",
    async (currentFiles: File[], { getState, dispatch }) => {
        const state = getState() as { dataImportWizard: DataImportWizardState };
        const existing = state.dataImportWizard.files;

        const existingByKey = new Map(existing.map((m) => [fileKey(m.file), m]));

        const keptMetas: UploadedFileMeta[] = [];
        const newFiles: File[] = [];

        currentFiles.forEach((f) => {
            const k = fileKey(f);
            const already = existingByKey.get(k);
            if (already) keptMetas.push(already);
            else newFiles.push(f);
        });

        const uploaded = newFiles.length ? await uploadEquipmentFiles(newFiles) : [];
        const merged = [...keptMetas, ...uploaded];

        dispatch(setFiles(merged));
        return true;
    }
);


export const validateAllFiles = createAsyncThunk(
    "dataImportWizard/validateAllFiles",
    async (_, { getState, dispatch }) => {
        const state = getState() as { dataImportWizard: DataImportWizardState };
        const files = state.dataImportWizard.files;
        const validations = state.dataImportWizard.validations;

        if (!files.length) return true;

        const neverValidated = files.filter((f) => !validations[f.id]?.result).map((f) => f.id);
        const toRevalidate = files
            .filter((f) => validations[f.id]?.result && validations[f.id].result!.isValid === false)
            .map((f) => f.id);

        // marca como rodando
        [...new Set([...neverValidated, ...toRevalidate])].forEach((fileId) => {
            dispatch(startFileValidation({ fileId }));
            dispatch(setFileValidationProgress({ fileId, progress: 30 }));
        });

        // 1) validate em lote para quem nunca validou
        if (neverValidated.length) {
            try {
                const resultsById = await validateEquipments(neverValidated);

                neverValidated.forEach((fileId) => {
                    const result = resultsById[fileId];
                    if (result) dispatch(finishFileValidation({ fileId, result }));
                    else dispatch(failFileValidation({ fileId, error: "Arquivo não retornou resultado de validação." }));
                });
            } catch (e: any) {
                const msg = e?.response?.data?.message || e?.message || "Erro ao validar arquivos.";
                neverValidated.forEach((fileId) => dispatch(failFileValidation({ fileId, error: msg })));
                throw e;
            }
        }

        // 2) revalidate por arquivo (precisa enviar rows editadas)
        for (const fileId of toRevalidate) {
            const latest = (getState() as any).dataImportWizard as DataImportWizardState;
            const rows = latest.validations[fileId]?.result?.rowsToFix ?? [];

            try {
                dispatch(startFileValidation({ fileId }));
                dispatch(setFileValidationProgress({ fileId, progress: 30 }));

                const result = await revalidateEquipment(fileId, rows);
                dispatch(finishFileValidation({ fileId, result }));
            } catch (e: any) {
                const msg = e?.response?.data?.message || e?.message || "Erro ao revalidar arquivo.";
                dispatch(failFileValidation({ fileId, error: msg }));
            }
        }

        return true;
    }
);


export const revalidateFile = createAsyncThunk(
    "dataImportWizard/revalidateFile",
    async ({ fileId }: { fileId: WizardFileId }, { getState, dispatch }) => {
        const state = getState() as { dataImportWizard: DataImportWizardState };
        const v = state.dataImportWizard.validations[fileId];
        const rows = v?.result?.rowsToFix ?? [];

        dispatch(startFileValidation({ fileId }));
        dispatch(setFileValidationProgress({ fileId, progress: 30 }));

        try {
            const result = await revalidateEquipment(fileId, rows);
            dispatch(finishFileValidation({ fileId, result }));
            return true;
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || "Erro ao revalidar arquivo.";
            dispatch(failFileValidation({ fileId, error: msg }));
            throw e;
        }
    }
);


export const importAllData = createAsyncThunk(
    "dataImportWizard/importAllData",
    async (_, { getState, dispatch }) => {
        const state = getState() as { dataImportWizard: DataImportWizardState };
        const files = state.dataImportWizard.files;

        if (!files.length) return true;

        dispatch(startImport());
        dispatch(setImportProgress(30));

        files.forEach((f) => {
            dispatch(startFileImport({ fileId: f.id }));
            dispatch(setFileImportProgress({ fileId: f.id, progress: 30 }));
        });

        try {
            const resultsById = await importEquipments(files.map((f) => f.id));

            files.forEach((f) => {
                const res = resultsById[f.id];
                if (res?.success) {
                    dispatch(finishFileImportSuccess({ fileId: f.id, message: res.message }));
                } else {
                    dispatch(finishFileImportFailed({ fileId: f.id, error: res?.message || "Falha ao importar." }));
                }
            });

            dispatch(setImportProgress(100));

            const allOk = files.every((f) => resultsById[f.id]?.success === true);
            if (allOk) dispatch(finishImportSuccess());
            else dispatch(finishImportFailed({ error: "Alguns arquivos não foram importados." }));

            return true;
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || "Erro ao importar arquivos.";
            dispatch(finishImportFailed({ error: msg }));
            files.forEach((f) => dispatch(finishFileImportFailed({ fileId: f.id, error: msg })));
            throw e;
        }
    }
);

const slice = createSlice({
    name: "dataImportWizard",
    initialState,
    reducers: {
        setFiles(state, action: PayloadAction<UploadedFileMeta[]>) {
            state.files = action.payload;

            // reset validações/import ao subir novos arquivos
            state.validations = {};
            state.imports = {};
            state.import = { status: "idle", progress: 0, done: false };

            action.payload.forEach(f => {
                state.validations[f.id] = { status: "idle", progress: 0 };
                state.imports[f.id] = { status: "idle", progress: 0 } as FileImportState;
            });
        },

        clearFiles(state) {
            state.files = [];
            state.validations = {};
            state.imports = {};
            state.import = { status: "idle", progress: 0, done: false };
        },

        removeFile(state, action: PayloadAction<{ fileId: WizardFileId }>) {
            const { fileId } = action.payload;

            state.files = state.files.filter(f => f.id !== fileId);
            delete state.validations[fileId];
            delete state.imports[fileId];

            // reseta import geral quando mexe em arquivos
            state.import = { status: "idle", progress: 0, done: false };

            // se não sobrou arquivo, zera tudo
            if (!state.files.length) {
                state.validations = {};
                state.imports = {};
            }
        },

        // ------------------- VALIDAÇÃO -------------------
        startFileValidation(state, action: PayloadAction<{ fileId: WizardFileId }>) {
            const v = state.validations[action.payload.fileId] ?? { status: "idle", progress: 0 };
            v.status = "running";
            v.progress = 0;
            v.error = undefined;
            state.validations[action.payload.fileId] = v;
        },

        setFileValidationProgress(
            state,
            action: PayloadAction<{ fileId: WizardFileId; progress: number }>
        ) {
            const v = state.validations[action.payload.fileId];
            if (!v) return;
            v.progress = action.payload.progress;
        },

        finishFileValidation(
            state,
            action: PayloadAction<{ fileId: WizardFileId; result: any }>
        ) {
            const v = state.validations[action.payload.fileId];
            if (!v) return;
            v.status = "succeeded";
            v.progress = 100;
            v.result = action.payload.result;
        },

        // Edição de célula na tabela editável
        updateCell(
            state,
            action: PayloadAction<{
                fileId: WizardFileId;
                rowId: string;
                field: EquipmentField;
                value: EquipmentRowData[EquipmentField] | any;
            }>
        ) {
            const { fileId, rowId, field, value } = action.payload;
            const v = state.validations[fileId];
            if (!v?.result) return;

            const row = v.result.rowsToFix.find(r => r.rowId === rowId);
            if (row) {
                row.data = { ...(row.data as any), [field]: value } as EquipmentRowData;
            }

            v.result.issues = v.result.issues.map(iss => {
                if (iss.rowId === rowId && iss.field === field) {
                    const resolved = value !== null && value !== undefined && String(value).trim() !== "";
                    return { ...iss, resolved };
                }
                return iss;
            });
        },

        // ------------------- IMPORTAÇÃO -------------------
        startImport(state) {
            state.import.status = "running";
            state.import.progress = 0;
            state.import.done = false;
            state.import.error = undefined;

            // reseta por arquivo
            state.files.forEach(f => {
                state.imports[f.id] = { status: "idle", progress: 0 };
            });
        },

        setImportProgress(state, action: PayloadAction<number>) {
            state.import.progress = action.payload;
        },

        finishImportSuccess(state) {
            state.import.status = "succeeded";
            state.import.progress = 100;
            state.import.done = true;
            state.import.error = undefined;
        },

        finishImportFailed(state, action: PayloadAction<{ error: string }>) {
            state.import.status = "failed";
            state.import.done = false;
            state.import.error = action.payload.error;
        },

        startFileImport(state, action: PayloadAction<{ fileId: WizardFileId }>) {
            const imp = state.imports[action.payload.fileId] ?? { status: "idle", progress: 0 };
            imp.status = "running";
            imp.progress = 0;
            imp.error = undefined;
            imp.result = undefined;
            state.imports[action.payload.fileId] = imp;
        },

        setFileImportProgress(state, action: PayloadAction<{ fileId: WizardFileId; progress: number }>) {
            const imp = state.imports[action.payload.fileId];
            if (!imp) return;
            imp.progress = action.payload.progress;
        },

        finishFileImportSuccess(state, action: PayloadAction<{ fileId: WizardFileId; message: string }>) {
            const imp = state.imports[action.payload.fileId];
            if (!imp) return;
            imp.status = "succeeded";
            imp.progress = 100;
            imp.result = { success: true, message: action.payload.message };
            imp.error = undefined;
        },

        finishFileImportFailed(state, action: PayloadAction<{ fileId: WizardFileId; error: string }>) {
            const imp = state.imports[action.payload.fileId];
            if (!imp) return;
            imp.status = "failed";
            imp.progress = 100;
            imp.result = { success: false, message: action.payload.error };
            imp.error = action.payload.error;
        },
        failFileValidation(state, action: PayloadAction<{ fileId: WizardFileId; error: string }>) {
            const v = state.validations[action.payload.fileId] ?? { status: "idle", progress: 0 };
            v.status = "failed";
            v.progress = 100;
            v.error = action.payload.error;
            state.validations[action.payload.fileId] = v;
        },

    }
});

export const {
    setFiles,
    clearFiles,
    removeFile,

    startFileValidation,
    setFileValidationProgress,
    finishFileValidation,
    updateCell,

    startImport,
    setImportProgress,
    finishImportSuccess,
    finishImportFailed,

    startFileImport,
    setFileImportProgress,
    finishFileImportSuccess,
    finishFileImportFailed,
    failFileValidation
} = slice.actions;

export default slice.reducer;

// ---------------- Selectors ----------------
export const selectWizardFiles = (s: any) => (s.dataImportWizard as DataImportWizardState).files;
export const selectValidations = (s: any) => (s.dataImportWizard as DataImportWizardState).validations;

export const selectImportsByFile = (s: any) => (s.dataImportWizard as DataImportWizardState).imports;
export const selectImportState = (s: any) => (s.dataImportWizard as DataImportWizardState).import;

export const selectAllValidated = (s: any) => {
    const st = s.dataImportWizard as DataImportWizardState;
    if (!st.files.length) return false;

    return st.files.every(f => {
        const v = st.validations[f.id];
        return v?.status === "succeeded" && v.result?.isValid === true;
    });
};

export const selectAllImported = (s: any) => {
    const st = s.dataImportWizard as DataImportWizardState;
    if (!st.files.length) return false;

    return st.files.every(f => {
        const imp = st.imports[f.id];
        return imp?.status === "succeeded" && imp.result?.success === true;
    });
};

export const selectIsValidating = (s: any) => {
    const st = s.dataImportWizard as DataImportWizardState;
    if (!st.files.length) return false;
    return st.files.some(f => st.validations[f.id]?.status === "running");
};

